import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { CurecallBanner, CurecallTooltip } from '@honestica/curecall-ui'
import { TPatientInteropLog } from './interop-log.type'
import { PatientService } from 'src/services/patient.service'
import { TimezoneAwareTime } from 'src/components/timezone-aware-time/timezone-aware-time.component'

export type TInteropLogProcessingInfoComponentProps = {
  interopLog: TPatientInteropLog | undefined
}

export function InteropLogProcessingInfoComponent({
  interopLog,
}: Readonly<TInteropLogProcessingInfoComponentProps>) {
  const { t } = useTranslation()
  const params = useParams<{ projectId: string; patientId: string }>()

  const projectIdNum = params?.projectId ? Number(params.projectId) : undefined
  const patientIdNum = params?.patientId ? Number(params.patientId) : undefined

  const downloadMutation = useMutation({
    mutationKey: ['download-interop-original', projectIdNum, patientIdNum, interopLog?.id],
    mutationFn: async () => {
      if (!interopLog || !projectIdNum || !patientIdNum) throw new Error('Missing identifiers')
      return PatientService.downloadInteropOriginal(projectIdNum, patientIdNum, interopLog.id)
    },
    onSuccess: ({ blob, filename }) => {
      try {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename || interopLog?.s3filename || `interop-${interopLog?.id}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        // no-op; in worst case the user can retry
      }
    },
  })

  if (!interopLog) {
    return <div>{t('components.patient.interop-logs.no-data')}</div>
  }

  return (
    <div className="c-m-1 c-rounded-lg c-grow c-bg-white c-px-2 c-py-1 c-border c-border-grey-500">
      <div className="c-flex c-gap-2 c-items-center c-justify-center c-my-2">
        <h5 className="c-text-center">
          {t('components.patient.interop-logs.curecall-processing.section-title')}
        </h5>
        {interopLog.CompositeFromInteropLogId > 0 && (
          <CurecallTooltip
            content={t('components.patient.interop-logs.composite-tooltip')}
            tooltipPosition="bottom"
          >
            <div className="c-rounded-full c-bg-blue-200 c-px-2">Composite</div>
          </CurecallTooltip>
        )}
      </div>

      <p className="c-mb-1 c-font-light c-font-nexa">InteropLogId: {interopLog.id}</p>
      {interopLog.payload?.messageId && (
        <p className="c-mb-1 c-font-light c-font-nexa">MessageId: {interopLog.payload?.messageId}</p>
      )}
      {interopLog.payload?.messageType && (
        <p className="c-mb-1 c-font-light c-font-nexa">MessageType: {interopLog.payload?.messageType}</p>
      )}
      <p className="c-mb-1 c-font-light c-font-nexa">Hydrated: {interopLog.isHydrated ? 'true' : 'false'}</p>
      {interopLog.CompositeFromInteropLogId > 0 && (
        <p className="c-mb-1 c-font-light c-font-nexa">
          Composite from: {interopLog.CompositeFromInteropLogId}
        </p>
      )}
      <p className="c-mb-1 c-font-light c-font-nexa">
        {t('components.patient.interop-logs.curecall-processing.timestamps-generated')}{' '}
        <TimezoneAwareTime date={interopLog.payload?.timestamp} formatter="dateWithHours" displaySeconds />
        {t('components.patient.interop-logs.curecall-processing.timestamps-received')}{' '}
        <TimezoneAwareTime date={interopLog.createdAt} formatter="dateWithHours" displaySeconds />
        {t('components.patient.interop-logs.curecall-processing.timestamps-processed')}{' '}
        <TimezoneAwareTime date={interopLog.processedAt} formatter="dateWithHours" displaySeconds />
      </p>
      <p className="c-mb-1 c-font-light c-font-nexa">
        {t('components.patient.interop-logs.curecall-processing.origin', { origin: interopLog.origin })}
      </p>
      <p className="c-mb-1 c-font-light c-font-nexa c-flex c-items-center c-gap-2 c-flex-wrap">
        <span>
          {t('components.patient.interop-logs.curecall-processing.s3filename', {
            s3filename: interopLog.s3filename,
          })}
        </span>
        {interopLog.s3filename && projectIdNum && patientIdNum && (
          <button
            type="button"
            onClick={() => downloadMutation.mutate()}
            disabled={downloadMutation.isPending}
            className="c-text-blue-600 hover:c-underline c-text-sm disabled:c-text-grey-500 disabled:hover:c-no-underline"
            title="Download original file"
          >
            {downloadMutation.isPending ? 'Downloading…' : 'Download'}
          </button>
        )}
      </p>
      {downloadMutation.isError ? (
        <div className="c-mt-1 c-text-red-600 c-text-xs" role="alert" aria-live="polite">
          failed: {downloadMutation.error.message}
        </div>
      ) : null}
      {interopLog.payload?.eventAuthor ? (
        <p className="c-mb-1 c-font-light c-font-nexa">
          Author: {interopLog.payload?.eventAuthor.id} {interopLog.payload?.eventAuthor.firstname || ''}{' '}
          {interopLog.payload?.eventAuthor.lastname || ''}
        </p>
      ) : null}

      {interopLog.errors && (
        <CurecallBanner appearance={'ERROR'}>
          {interopLog.errors?.map((err) => (
            <span key={err.type + err.error + err.remoteId} className="c-mb-0">
              {err.type} {err.remoteId ? `(${err.remoteId})` : ''}: {err.error}
            </span>
          ))}
        </CurecallBanner>
      )}
    </div>
  )
}
